import { registerLocaleData } from '@angular/common';
import localeEnGb from '@angular/common/locales/en-GB';
import {
    AfterViewInit,
    Component,
    computed,
    CUSTOM_ELEMENTS_SCHEMA,
    DestroyRef,
    effect,
    inject,
    Injector,
    linkedSignal,
    OnInit,
    Signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { RouterOutlet } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { BusinessServices, IUser } from '@shs-digi-care-monorepo/interfaces';
import { filter, take, tap } from 'rxjs';
import type { UserInfoDialogComponent } from './shared/components/user-info-dialog/user-info-dialog.component';
import type {
    UserInfoDialogData,
    UserInfoDialogResult
} from './shared/components/user-info-dialog/user-info-dialog.model';
import { HeaderComponent } from './shared/components/header/header.component';
import {
    ActionRequiredComponent,
    BannerComponent,
    FooterComponent,
    WrongRegionComponent
} from '@shs-digi-care-monorepo/frontend-lib/components';
import { EnvironmentHintService } from '@shs-digi-care-monorepo/frontend-lib/services';
import { environment } from '../environments/environment';
import { ShSnackbarContainerComponent } from '@shs-digi-care-monorepo/frontend-lib/ui/sh-snackbar/sh-snackbar-container.component';
import { TeamActions, UserActions, UserState } from '@shs-digi-care-monorepo/frontend-lib/states';
import { LoggingService } from '@shs-digi-care-monorepo/frontend-lib/logging';
import { Region, wrongRegionOf } from '@shs-digi-care-monorepo/frontend-lib/util';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { DEFAULT_LANGUAGE } from './app.config';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    imports: [
        RouterOutlet,
        FooterComponent,
        HeaderComponent,
        ShSnackbarContainerComponent,
        ActionRequiredComponent,
        BannerComponent,
        WrongRegionComponent
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AppComponent implements OnInit, AfterViewInit {
    private store = inject(Store);
    private readonly translate: TranslateService = inject(TranslateService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly logger = inject(LoggingService);
    protected readonly injector = inject(Injector);
    protected readonly dialog = inject(MatDialog);

    protected readonly stage = environment.stage;
    protected readonly showEnvironmentHint = inject(EnvironmentHintService).showEnvironmentHint;
    protected readonly me: Signal<IUser | null> = this.store.selectSignal(UserState.me);
    private readonly userLoadedFromState: Signal<boolean> = this.store.selectSignal(UserState.loaded);
    /**
     * Latches to `true` once the user has been loaded for the first time and never
     * reverts to `false`, so the startup spinner is shown only once and never reappears
     * during navigation or any subsequent state changes.
     */
    protected readonly userLoaded: Signal<boolean> = linkedSignal<boolean, boolean>({
        source: this.userLoadedFromState,
        computation: (loaded, previous) => previous?.value || loaded
    });
    /**
     * Region the user is pointed to when their roles do not cover the NAM stream,
     * in which case the application is replaced by the wrong region notice.
     */
    protected readonly wrongRegion: Signal<Region | null> = computed(() =>
        wrongRegionOf(this.me(), BusinessServices.OM_NAM)
    );

    constructor() {
        this.loadTeams();
    }

    public ngOnInit(): void {
        this.logInitialization();
        this.setDefaultLanguage();
        this.listenToLanguageChanges();
        this.registerLocales();
    }

    public ngAfterViewInit(): void {
        this.displayIncompleteProfileInfoPopup();
    }

    private logInitialization(): void {
        this.logger.info('Application initialized');
    }

    private setDefaultLanguage(): void {
        this.translate.setDefaultLang(DEFAULT_LANGUAGE);
        this.translate.use(DEFAULT_LANGUAGE);
    }

    private listenToLanguageChanges(): void {
        this.translate.onLangChange
            .pipe(
                tap((event) => {
                    this.logger.debug('Language changed', { lang: event.lang });
                    localStorage.setItem('language', event.lang);
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    private loadTeams(): void {
        inject(OidcSecurityService)
            .isAuthenticated()
            .pipe(
                filter((isAuthenticated) => isAuthenticated),
                take(1),
                tap(() => {
                    this.store.dispatch(new TeamActions.LoadAll());
                })
            )
            .subscribe();
    }

    private registerLocales(): void {
        registerLocaleData(localeEnGb);
    }

    private displayIncompleteProfileInfoPopup(): void {
        effect(
            async () => {
                const user = this.me();
                const profileSetupLater = sessionStorage.getItem('profileSetupLater');
                if (user && !this.wrongRegion() && !user.userProfileComplete && profileSetupLater !== 'true') {
                    const { UserInfoDialogComponent } = await import(
                        './shared/components/user-info-dialog/user-info-dialog.component'
                    );
                    this.dialog
                        .open<UserInfoDialogComponent, UserInfoDialogData, UserInfoDialogResult>(
                            UserInfoDialogComponent,
                            { data: user }
                        )
                        .afterClosed()
                        .pipe(
                            tap((result) => {
                                if (result) {
                                    this.store.dispatch(new UserActions.PatchMe(result));
                                } else {
                                    sessionStorage.setItem('profileSetupLater', 'true');
                                }
                            })
                        )
                        .subscribe();
                }
            },
            { injector: this.injector }
        );
    }
}
